import { Outlet, useRouterState, Navigate } from "@tanstack/react-router";
import { AppSidebar } from "./app-sidebar";
import { AppHeader } from "./app-header";
import { Forbidden403 } from "./forbidden-403";
import { useAuth } from "@/contexts/auth-context";
import { canAccessPath } from "@/lib/route-permissions";

export function AppLayout() {
  const { user, permissions, sessionExpiredReason, isBootstrapping } = useAuth();
  const path = useRouterState({ select: (s) => s.location.pathname });

  if (path.startsWith("/login")) {
    return <Outlet />;
  }

  // ADR-016: cookie auth is HttpOnly so the FE cannot read the session
  // synchronously. While the /api/me bootstrap is in flight, hold the
  // render — redirecting to /login would create a flash of the login
  // form on every page reload for an authenticated user. Once
  // bootstrap settles, either render the protected tree or redirect.
  if (isBootstrapping && !user) {
    return (
      <div
        className="flex min-h-screen w-full items-center justify-center bg-background"
        aria-busy="true"
      />
    );
  }

  if (!user) {
    if (sessionExpiredReason) {
      return <Navigate to="/login" search={{ reason: sessionExpiredReason }} replace />;
    }
    return <Navigate to="/login" replace />;
  }

  // US-055 — forced first-login password change. The change-password
  // screen renders chrome-free (no sidebar/header) so a flagged user
  // cannot navigate away until the temporary password is rotated. A user
  // who is NOT flagged may still reach it voluntarily (self-service).
  if (path.startsWith("/change-password")) {
    return (
      <div className="min-h-screen w-full bg-background">
        <Outlet />
      </div>
    );
  }
  if (user.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }

  // US-003c — role-based route guard. Re-evaluates on every navigation
  // (useRouterState subscribes the layout to path changes). If the
  // signed-in user's role can't see this path, render Forbidden403 in
  // place of the route's <Outlet />. Sidebar + header still render —
  // the 403 sits in the main slot so the user can navigate elsewhere.
  const accessible = canAccessPath(permissions.nav_items, path);

  return (
    <div className="flex min-h-screen w-full bg-background">
      <AppSidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <AppHeader />
        <main className="flex-1 p-4 lg:p-8">{accessible ? <Outlet /> : <Forbidden403 />}</main>
      </div>
    </div>
  );
}
